import { createContext } from 'react';

export const SELECTED_ORG_AUTH0_ID_KEY = 'delogue-selected-org-auth0-id';

// Cross-app "which org is currently active" signal (DL-61837, R6). Written by the legacy
// LegacySessionBridgeController on every org-scoped bridge round trip (both the classic-embed
// iframe re-mint and the legacy "Switch Company" flow), inheriting that environment's shared
// <httpCookies domain=".{sub}.delogue.com"> default — the same mechanism designerOrgInfoV2 and
// supplierPortalInfo already rely on to be readable from beta.{sub}.delogue.com. D2 mirrors that
// domain explicitly here so both surfaces read/write the identical cookie: whichever surface last
// switched org wins, and the other follows on its next document load (Testmo 2507007).
export const ACTIVE_ORG_COOKIE_NAME = 'activeOrgAuth0Id';

// Sentinel value the LEGACY side writes into the cookie when the user switches to a login
// context that has no Auth0 organization at all (a legacy supplier account, or an org that
// was never synced to Auth0). Such a context is not representable in D2, so D2 cannot
// "follow" the switch — instead it must sign out of its org-scoped session entirely
// (decided on DL-61837): clear the persisted selection, tokens and cached data, and land on
// the org picker. The Auth0 session itself is deliberately left alive — terminating it would
// back-channel-log-out the very legacy session the user just switched to.
// Must match LoginPageController.ActiveOrgUnsupportedSentinel (backend).
export const ACTIVE_ORG_UNSUPPORTED_SENTINEL = 'unsupported';

const ACTIVE_ORG_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30; // parity with LoginCookieExpirationDays

export const readActiveOrgCookie = (): string | null => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${ACTIVE_ORG_COOKIE_NAME}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

export const writeActiveOrgCookie = (auth0OrgId: string): void => {
  // beta.{sub}.delogue.com -> .{sub}.delogue.com, matching legacy's <httpCookies> domain exactly
  // (strip only the leading "beta." label so this generalizes to every tenant/environment).
  const sharedDomain = window.location.hostname.replace(/^beta\./, '');
  // No `secure` flag: every real deployment is HTTPS-only anyway (nothing to protect it from),
  // and this is not a credential — same reasoning as legacy's designerOrgInfoV2/supplierPortalInfo,
  // which likewise rely on the environment's shared, non-HttpOnly <httpCookies> default.
  document.cookie = [
    `${ACTIVE_ORG_COOKIE_NAME}=${encodeURIComponent(auth0OrgId)}`,
    `domain=.${sharedDomain}`,
    'path=/',
    `max-age=${ACTIVE_ORG_COOKIE_MAX_AGE_SECONDS}`,
    'samesite=lax',
  ].join('; ');
};

// Expires the cookie client-side (mirrors the backend's own logout-path cleanup in
// LoginPageController.Logout()/BetaLoginController.RemoveAuthenticationCookies). D2's
// clearSelectedOrg calls this so a user backing out of an org — or the SDK-only logout fallback
// when /betalogin/logout itself fails — can't leave a stale value for the next browser session to
// auto-adopt (Greptile catch on PR #14018).
export const clearActiveOrgCookie = (): void => {
  const sharedDomain = window.location.hostname.replace(/^beta\./, '');
  document.cookie = [
    `${ACTIVE_ORG_COOKIE_NAME}=`,
    `domain=.${sharedDomain}`,
    'path=/',
    'max-age=0',
    'samesite=lax',
  ].join('; ');
};

export type SelectedOrgContextType = {
  selectedOrgAuth0Id: string | null;
  orgToken: string | null;
  selectOrg: (auth0OrgId: string) => void;
  clearSelectedOrg: () => void;
  // True from the moment useLogout starts until the browser navigates away. Logout clears the
  // selected org while the /betalogin/logout round trip is still in flight, and without this flag
  // AuthLoaderFederated interprets "authenticated + no org" as "show the account selector" — the
  // selector flashed for a few seconds mid-logout (Slack report, DL-61837). While logging out the
  // shell must render a plain loader instead.
  isLoggingOut: boolean;
  beginLogout: () => void;
  // Auth0 org ids that have already failed to resolve to a Delogue user this session
  // (accessible-orgs validation, or the user backing out of an unresolvable org). Consumers
  // that auto-select an org (OrgProvider's JWT-derived auto-detect, SwitchUserFederated's
  // single-org auto-select) must skip these or they loop straight back into the same failure.
  rejectedOrgIds: Set<string>;
  // DL-64690: a superadmin session handed over from legacy. selectedOrgAuth0Id stays null on
  // purpose — the superadmin is not a member of the customer's Auth0 org, so iframes must not
  // carry _auth0org — which means the org gates have to be skipped explicitly instead.
  isImpersonating: boolean;
};

export const SelectedOrgContext = createContext<SelectedOrgContextType>({
  selectedOrgAuth0Id: null,
  orgToken: null,
  selectOrg: () => undefined,
  clearSelectedOrg: () => undefined,
  rejectedOrgIds: new Set(),
  isLoggingOut: false,
  beginLogout: () => undefined,
  isImpersonating: false,
});
